import { useId, useMemo, useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CHIP_TONE } from "./chipTone";

/** One watchlist the builder can take in, of whichever kind it's stored as. */
export interface PickerRow {
  /** Unique across kinds: "p-12", "t-3", "c-40". */
  key: string;
  kind: "watchlist" | "tag" | "combined";
  id: number;
  name: string;
  color?: string | null;
  owner: "mine" | "desk";
  checked: boolean;
  contents: {
    tickers: string[];
    keywords: string[];
    /** Tag names, as far as they're known. */
    tags: string[];
    expression?: string;
  };
}

interface ListPickerProps {
  rows: PickerRow[];
  onToggle: (row: PickerRow) => void;
  /** A row opened to show what it holds, so its tag names can be looked up. */
  onExpand?: (row: PickerRow) => void;
}

const BLOCKS = [
  { owner: "mine", title: "Your watchlists" },
  { owner: "desk", title: "Newsquawk watchlists" },
] as const;

/** How many of a list's terms an opened row shows before "+N more". */
const PREVIEW = 24;

/**
 * The watchlists to build from, yours then Newsquawk's, in one column.
 *
 * Only watchlists: tags are added in the Tags box, where they're searched and
 * browsed with their kind. A row is a checkbox, the list's colour and name,
 * and a chevron that opens it to show what it holds. How a list happens to be
 * stored — tickers, keywords, tags, combined — isn't something to pick by, so
 * it isn't shown; its terms are, when opened.
 */
export default function ListPicker({ rows, onToggle, onExpand }: ListPickerProps) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const baseId = useId();
  const q = query.trim().toLowerCase();

  const blocks = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    return BLOCKS.map((block) => {
      const own = rows.filter((r) => r.owner === block.owner);
      return {
        ...block,
        ticked: own.filter((r) => r.checked).length,
        rows: own
          .filter((r) => !q || r.name.toLowerCase().includes(q))
          .sort((a, b) => collator.compare(a.name, b.name)),
        total: own.length,
      };
    }).filter((block) => (q ? block.rows.length > 0 : block.total > 0));
  }, [rows, q]);

  return (
    <aside
      aria-label="Watchlists you can add"
      className="flex w-[300px] shrink-0 flex-col border-r border-gray-800 xl:w-[320px]"
    >
      <div className="shrink-0 p-3">
        <div className="relative">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-[9px] size-4 text-gray-500"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a watchlist"
            aria-label="Find a watchlist"
            className="h-[34px] w-full rounded-md border border-gray-700 bg-gray-800 px-[34px] text-[13px] text-gray-100 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none focus:ring-1 focus:ring-squawk-blue"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear the search"
              className="absolute right-[5px] top-[5px] flex size-6 items-center justify-center rounded border-0 bg-transparent p-0 text-gray-400 hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
            >
              <XMarkIcon className="size-3.5" strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-5 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {blocks.map((block) => {
          const open = q ? true : !collapsed[block.owner];
          const panelId = `${baseId}-${block.owner}`;
          return (
            <section key={block.owner} className="mb-2 flex flex-col">
              <div className="sticky top-0 z-[1] bg-gray-900">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((prev) => ({ ...prev, [block.owner]: !prev[block.owner] }))
                  }
                  aria-expanded={open}
                  aria-controls={panelId}
                  className="flex h-9 w-full items-center gap-2 rounded-none border-0 bg-transparent px-3 py-0 text-left text-[13px] font-semibold text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-squawk-blue/70"
                >
                  <span className="flex-1">{block.title}</span>
                  {block.ticked > 0 && (
                    <span className="text-xs font-medium text-squawk-blue">{block.ticked} ticked</span>
                  )}
                  <Chevron open={open} />
                </button>
              </div>
              {open && (
                <ul id={panelId} className="m-0 flex list-none flex-col p-0">
                  {block.rows.map((row) => (
                    <PickerItem
                      key={row.key}
                      row={row}
                      inputId={`${baseId}-${row.key}`}
                      expanded={!!expanded[row.key]}
                      onToggle={() => onToggle(row)}
                      onExpand={() => {
                        const next = !expanded[row.key];
                        setExpanded((prev) => ({ ...prev, [row.key]: next }));
                        if (next) onExpand?.(row);
                      }}
                    />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
        {q && blocks.length === 0 && (
          <p className="mx-4 my-3 text-[13px] text-gray-400">
            No watchlist matches “{query.trim()}”.
          </p>
        )}
        {!q && blocks.length === 0 && (
          <p className="mx-4 my-3 text-[13px] text-gray-400">
            Watchlists you save show here, ready to combine.
          </p>
        )}
      </div>
    </aside>
  );
}

/**
 * One watchlist row: checkbox, colour, name, and a chevron opening what it
 * holds. Shared with the onboarding watchlists step, so a list looks the same
 * wherever it's picked.
 */
export function PickerItem({
  row,
  inputId,
  expanded,
  onToggle,
  onExpand,
}: {
  row: PickerRow;
  inputId: string;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
}) {
  const { tickers, keywords, tags, expression } = row.contents;
  const terms = [
    ...tickers.map((label) => ({ label, tone: CHIP_TONE.ticker })),
    ...keywords.map((label) => ({ label, tone: CHIP_TONE.keyword })),
    ...(expression ? [{ label: expression, tone: CHIP_TONE.keyword }] : []),
  ];
  const shown = terms.slice(0, PREVIEW);
  const more = terms.length - shown.length;
  const empty = terms.length === 0 && tags.length === 0;

  return (
    <li className={clsx("flex flex-col", row.checked && "bg-squawk-blue/10")}>
      <div className="flex min-h-[34px] items-center gap-2 pl-3 pr-1.5">
        <input
          id={inputId}
          type="checkbox"
          checked={row.checked}
          onChange={onToggle}
          className="size-3.5 shrink-0 cursor-pointer rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-2 focus:ring-squawk-blue/60 focus:ring-offset-0"
        />
        <label htmlFor={inputId} className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-2">
          {row.color && (
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: row.color }}
            />
          )}
          <span className="truncate text-[13px] text-gray-200">{row.name}</span>
        </label>
        <button
          type="button"
          onClick={onExpand}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Hide" : "Show"} what's in ${row.name}`}
          className="flex size-7 shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-gray-400 hover:bg-gray-800 hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
        >
          <Chevron open={expanded} />
        </button>
      </div>
      {expanded && (
        <div className="flex flex-wrap gap-1 pb-2.5 pl-[34px] pr-3">
          {shown.map((term, i) => (
            <span
              key={`${term.label}-${i}`}
              className={clsx(
                "inline-flex h-[22px] items-center whitespace-nowrap rounded border px-1.5 text-xs",
                term.tone
              )}
            >
              {term.label}
            </span>
          ))}
          {more > 0 && (
            <span className="inline-flex h-[22px] items-center px-1 text-xs text-gray-400">
              +{more.toLocaleString()} more
            </span>
          )}
          {tags.map((name, i) => (
            <span
              key={`tag-${name}-${i}`}
              className={clsx(
                "inline-flex h-[22px] items-center whitespace-nowrap rounded border px-1.5 text-xs",
                CHIP_TONE.tag
              )}
            >
              {name}
            </span>
          ))}
          {empty && <span className="text-xs text-gray-500">Nothing in it yet</span>}
        </div>
      )}
    </li>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronDownIcon
      aria-hidden="true"
      strokeWidth={2}
      className={clsx(
        "size-3.5 text-gray-400 transition-transform duration-150 motion-reduce:transition-none",
        open && "rotate-180"
      )}
    />
  );
}
